import { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { ConfirmationModal } from 'features/common/ui/ConfirmationModal'
import { RotateRightOutlined, DeleteOutlined, Button, notification, Progress, Typography } from 'shared/ui'
import { IFsNodeSchema } from '@cloudike/web_files'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { SkeletonAllAssets } from 'features/common/ui/SkeletonAllAssets'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { useTranslation } from 'react-i18next'

import {
  emptyTrashThunk,
  fullRestoreThunk,
  deleteNodesThunk,
  restoreNodesThunk,
  recentlyDeletedActions,
  RecentlyDeletedToolbarAction,
  fetchAllBoardsAndTrashedNodesThunk,
  fetchNextTrashedNodesThunk,
} from '../model/slice/recentlyDeletedSlice'
import { allTrashedNodesSelector, recentlyDeletedAreAllTrashedNodesFetchedSelector, recentlyDeletedStatusSelector } from '../model/selectors/recentlyDeletedSelectors'

import { RecentlyDeletedList } from './RecentlyDeletedList'
import { RecentlyDeletedToolbarTop } from './RecentlyDeletedToolbarTop'

type ModalContext =
  | { type: 'empty' }
  | { type: 'recover'; id: string; name: string }
  | { type: 'recoverAll' }
  | { type: 'recoverMultiple'; ids: string[] }
  | { type: 'delete'; id: string; name: string }
  | { type: 'deleteMultiple'; ids: string[] }

export const RecentlyDeleted = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const allTrashedNodes = useAppSelector(allTrashedNodesSelector)
  const areAllTrashedNodesFetched = useAppSelector(recentlyDeletedAreAllTrashedNodesFetchedSelector)
  const status = useAppSelector(recentlyDeletedStatusSelector)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalContext, setModalContext] = useState<ModalContext | null>(null)
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())

  const toolbarRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const toolbarActionsIcons = {
    [RecentlyDeletedToolbarAction.Recover]: <RotateRightOutlined />,
    [RecentlyDeletedToolbarAction.Delete]: <DeleteOutlined />,
  }

  useEffect(() => {
    dispatch(recentlyDeletedActions.resetState())
    dispatch(fetchAllBoardsAndTrashedNodesThunk())
  }, [dispatch])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        toolbarRef.current &&
        !toolbarRef.current.contains(target) &&
        listRef.current &&
        !listRef.current.contains(target)
      ) {
        setSelectedNodeIds(new Set())
      }
    }

    if (selectedNodeIds.size > 0) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [selectedNodeIds])

  const getNodeType = (id: string): 'Board' | 'Asset' => {
    const node = allTrashedNodes.find(n => n.id === id)
    return node?.type === 'dir' ? 'Board' : 'Asset'
  }

  const openRecoverModal = (id: string, name: string) =>
    setModal({ type: 'recover', id, name })

  const openRecoverMultipleModal = (ids: string[]) =>
    setModal({ type: 'recoverMultiple', ids })

  const openRecoverAllModal = () => setModal({ type: 'recoverAll' })

  const openEmptyModal = () => setModal({ type: 'empty' })

  const openDeleteModal = (id: string, name: string) =>
    setModal({ type: 'delete', id, name })

  const openDeleteMultipleModal = (ids: string[]) =>
    setModal({ type: 'deleteMultiple', ids })

  const setModal = (context: ModalContext) => {
    setModalContext(context)
    setModalOpen(true)
  }

  const clearModal = () => {
    setModalOpen(false)
    setModalContext(null)
  }

  const handleConfirm = async () => {
    if (!modalContext) return

    clearModal()
    const key = `progress-${Date.now()}`
    let progress = 0

    const updateProgress = () => {
      notification.open({
        key,
        message: getProgressMessage(modalContext),
        description: getProgressDescription(modalContext),
        duration: 0,
        placement: 'bottomRight',
        icon: <Progress percent={progress}
          type="circle"
          size={16}
          strokeColor="var(--color-primary)"
        />,
      })
    }

    const simulateProgress = () =>
      new Promise<void>(resolve => {
        const interval = setInterval(() => {
          progress += 15
          if (progress >= 95) {
            clearInterval(interval)
            resolve()
          } else {
            updateProgress()
          }
        }, 200)
      })

    try {
      updateProgress()
      await simulateProgress()

      const { type } = modalContext

      if (type === 'recover') {
        const node = allTrashedNodes.find(n => n.id === modalContext.id)
        if (node?.is_parent_trashed) {
          showNotification({
            type: NOTIFICATION_TYPES.WARNING,
            title: 'Cannot recover asset',
            message:
              'The parent board is also in the trash. Please restore the board first.',
          })
          return
        }

        await dispatch(restoreNodesThunk({
          nodeIds: [modalContext.id],
          nodeType: getNodeType(modalContext.id),
          notificationKey: key,
          successfullCallback: (id) => {
            dispatch(recentlyDeletedActions.removeNode(id))
          },
        })).unwrap()

      } else if (type === 'empty') {
        await dispatch(emptyTrashThunk({ notificationKey: key }) as any).unwrap()
        dispatch(recentlyDeletedActions.setAllTrashedNodes([]))

      } else if (type === 'recoverAll') {
        await dispatch(fullRestoreThunk({ notificationKey: key }) as any).unwrap()
        dispatch(recentlyDeletedActions.setAllTrashedNodes([]))

      } else if (type === 'recoverMultiple') {
        const validNodeIds: string[] = []
        const invalidNodes: IFsNodeSchema[] = []
        modalContext.ids.forEach(id => {
          const node = allTrashedNodes.find(n => n.id === id)
          if (node?.is_parent_trashed) {
            invalidNodes.push(node)
          } else {
            validNodeIds.push(id)
          }
        })

        if (invalidNodes.length > 0) {
          showNotification({
            type: NOTIFICATION_TYPES.WARNING,
            title: 'Some items cannot be recovered',
            message: `You must recover their parent boards first:\n${invalidNodes.map(n => `"${n.name}"`).join(', ')}`,
          })
        }
        
        if (validNodeIds.length > 0) {
          await dispatch(restoreNodesThunk({
            nodeIds: validNodeIds,
            notificationKey: key,
            successfullCallback: (id) => {
              dispatch(recentlyDeletedActions.removeNode(id))
            },
            nodeType: getNodeType(validNodeIds[0]),
          })).unwrap()
        }

      } else if (type === 'delete') {
        await dispatch(deleteNodesThunk({
          nodeIds: [modalContext.id],
          notificationKey: key,
        })).unwrap()
        dispatch(recentlyDeletedActions.removeNode(modalContext.id))

      } else if (type === 'deleteMultiple') {
        await dispatch(deleteNodesThunk({
          nodeIds: modalContext.ids,
          notificationKey: key,
        })).unwrap()
        dispatch(recentlyDeletedActions.removeMultipleNodes(modalContext.ids))
      }

    } catch (e) {
    } finally {
      clearModal()
    }
  }

  const handleToolbarActionClick = (
    actionId: string,
    selectedItems: IFsNodeSchema[]
  ) => {
    if (!selectedItems.length) return

    const ids = selectedItems.map(i => i.id)
    if (actionId === RecentlyDeletedToolbarAction.Recover) {
      selectedItems.length === 1
        ? openRecoverModal(ids[0], selectedItems[0].name)
        : openRecoverMultipleModal(ids)
    }

    if (actionId === RecentlyDeletedToolbarAction.Delete) {
      selectedItems.length === 1
        ? openDeleteModal(ids[0], selectedItems[0].name)
        : openDeleteMultipleModal(ids)
    }
  }

  const getModalTitle = () => {
    if (!modalContext) return ''
    switch (modalContext.type) {
    case 'empty': return 'Empty trash bin'
    case 'recover': return `Recover item?`
    case 'recoverAll': return 'Recover all items'
    case 'recoverMultiple': return `Recover ${modalContext.ids.length} items`
    case 'delete': return `Delete "${modalContext.name}"?`
    case 'deleteMultiple': return `Delete ${modalContext.ids.length} items`
    }
  }

  const getModalDescription = () => {
    if (!modalContext) return ''
    switch (modalContext.type) {
    case 'empty':
      return (
        <>
            Are you sure you want to delete all items from the trash bin?
          <br />
            This action will permanently delete all assets and boards and they won’t be recoverable.
        </>
      )
    case 'recover':
      return (
        <>
            Are you sure you want to recover “
          {modalContext.name}
            ”?

          <br />
            This will restore all versions of this item and return it to its original location.
        </>
      )
    case 'recoverAll':
      return 'This will restore all items from the trash bin and return them to their original location'
    case 'recoverMultiple':
      return `Do you really want to recover ${modalContext.ids.length} items?`
    case 'delete':
      return (
        <>
            Are you sure you want to permanently delete this item?
          <br />

          <br />
            This action cannot be undone — the item will be removed from your workspace permanently.
        </>
      )
    case 'deleteMultiple':
      return `Do you really want to delete ${modalContext.ids.length} items?`
    }
  }

  const getProgressMessage = (ctx: ModalContext): string => {
    switch (ctx.type) {
    case 'recover': return 'Recovering...'
    case 'delete': return 'Deleting...'
    case 'recoverMultiple': return 'Recovering items...'
    case 'deleteMultiple': return 'Deleting items...'
    case 'empty': return 'Emptying trash...'
    case 'recoverAll': return 'Recovering all items...'
    }
  }

  const getProgressDescription = (ctx: ModalContext): string => {
    switch (ctx.type) {
    case 'recover':
    case 'delete': {
      const type = getNodeType(ctx.id)
      return `${type}: ${ctx.name}`
    }
    case 'recoverMultiple':
    case 'deleteMultiple': {
      const type = getNodeType(ctx.ids[0])
      return `${type}: ${ctx.ids.length} items selected`
    }
    case 'empty':
    case 'recoverAll':
      return 'This might take a few moments.'
    }
  }

  return (
    <SPageContainer>
      {allTrashedNodes.length > 0 && (
        <>
          <SHeadingContainer>
            <Typography.Text type="secondary">
              Move unused assets or entire boards to the trash bin. They’ll stay here for 30 days in case you change your mind.
            </Typography.Text>
          </SHeadingContainer>

          <SButtonContainer>
            {/* <Button onClick={openRecoverAllModal}>
              Recover all
            </Button> */}            

            <Button onClick={openEmptyModal}>
              Empty trash bin
            </Button>
          </SButtonContainer>
        </>
      )}

      {status === LOADING_STATUSES.LOADING || status === LOADING_STATUSES.IDLE ? (<SkeletonAllAssets />) : (
        <RecentlyDeletedList
          items={allTrashedNodes}
          onRecover={openRecoverModal}
          onDelete={openDeleteModal}
          selectedRowIds={selectedNodeIds}
          onSelectionChange={setSelectedNodeIds}
          listRef={listRef}
          onScrollEnd={() => dispatch(fetchNextTrashedNodesThunk())}
          disableLoadMore={areAllTrashedNodesFetched}
        />
      )}

      <ConfirmationModal
        open={modalOpen}
        title={getModalTitle()}
        description={getModalDescription()}
        confirmText={
          modalContext?.type === 'empty'
            ? 'Empty'
            : modalContext?.type?.includes('delete')
              ? t('a_common_deleteForever')
              : t('a_common_recover')
        }
        cancelText={t('a_common_cancel')}
        onConfirm={handleConfirm}
        onCancel={clearModal}
      />

      <RecentlyDeletedToolbarTop
        toolbarRef={toolbarRef}
        selectedItems={allTrashedNodes.filter(n => selectedNodeIds.has(n.id))}
        actionsIcons={toolbarActionsIcons}
        onActionClick={handleToolbarActionClick}
        onClose={() => setSelectedNodeIds(new Set())}
      />
    </SPageContainer>
  )
}

const SPageContainer = styled.div``
const SButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`
const SHeadingContainer = styled.div`
  width: 600px;
  margin-bottom: 32px;
`
